import type {
    AlertsWidgetViewModel,
    ScannerWidgetViewModel,
    ScreenerWidgetViewModel,
    SearchAlphaWidgetViewModel,
    LocatesV2WidgetViewModel,
} from '../types';
import type { Idea, ScannerPlan, ScreenerPlan } from '@thinkalpha/platform-ws-client/contracts/ideas/index.js';
import { BinaryLogicalOperator } from '@thinkalpha/ui-types/contracts/language';

export const createDefaultSearchAlphaWidget = (
    values: Partial<SearchAlphaWidgetViewModel> = {},
): SearchAlphaWidgetViewModel => {
    return {
        type: 'searchalpha',
        channelId: null,
        displayingResults: true,
        displayingForm: true,
        displayingIdeas: true,
        splitPercentage: 50,
        filter: null,
        universeId: null,
        columnTemplate: null,
        scrolling: false,
        idea: {
            name: null,
            id: undefined,
            key: 'ticker',
            plan: {
                type: 'if-then',
                root: {
                    type: 'group',
                    id: 'indicator',
                    collapsed: false,
                    description: null,
                    enabled: true,
                    name: null,
                    lines: [],
                    operator: BinaryLogicalOperator.and,
                },
                imports: [],
            },
            defaultColumnTemplateId: null,
            defaultUniverseId: null,
            description: null,
            isTemplate: false,
        },
        ...values,
    };
};

export const createDefaultScreenerWidget = (values: Partial<ScreenerWidgetViewModel> = {}): ScreenerWidgetViewModel => {
    const defaultIdea: Idea & { plan: ScreenerPlan } = {
        name: null,
        id: undefined,
        description: null,
        defaultUniverseId: null,
        defaultColumnTemplateId: null,
        isTemplate: false,
        key: 'ticker',
        plan: {
            type: 'screener',
            mode: 'include-all',
            conditions: [],
            excludeList: [],
            includeList: [],
        },
    };

    return {
        type: 'screener',
        channelId: values.channelId ?? null,
        displayingResults: values.displayingResults ?? true,
        displayingForm: values.displayingForm ?? true,
        splitPercentage: values.splitPercentage ?? 50,
        filter: values.filter ?? null,
        columnTemplate: values.columnTemplate ?? null,
        columnOptions: values.columnOptions,
        scrolling: values.scrolling ?? false,
        idea: values.idea ?? defaultIdea,
        isIdeaDirty: values.isIdeaDirty,
        universeId: values.universeId ?? null,
        ideaHydration: values.ideaHydration,
    };
};

export const createDefaultScannerWidget = (values: Partial<ScannerWidgetViewModel> = {}): ScannerWidgetViewModel => {
    const defaultIdea: Idea & { plan: ScannerPlan } = {
        name: null,
        id: undefined,
        description: null,
        defaultUniverseId: null,
        defaultColumnTemplateId: null,
        isTemplate: false,
        key: 'ticker',
        plan: {
            type: 'scanner',
            mode: 'include-all',
            conditions: [],
        },
    };

    return {
        type: 'scanner',
        channelId: values.channelId ?? null,
        displayingResults: values.displayingResults ?? true,
        displayingForm: values.displayingForm ?? true,
        splitPercentage: values.splitPercentage ?? 50,
        filter: values.filter ?? null,
        columnTemplate: values.columnTemplate ?? null,
        columnOptions: values.columnOptions,
        scrolling: values.scrolling ?? false,
        universeId: values.universeId ?? null,
        idea: values.idea ?? defaultIdea,
        isIdeaDirty: values.isIdeaDirty,
        defaultIdeaStatus: values.defaultIdeaStatus,
        ideaHydration: values.ideaHydration,
    };
};

export const createDefaultLocatesV2Widget = (
    values: Partial<LocatesV2WidgetViewModel> = {},
): LocatesV2WidgetViewModel => {
    return {
        type: 'lab',
        channelId: values.channelId ?? null,
        displayingQuote: values.displayingQuote ?? true,
        displayingResults: values.displayingResults ?? true,
        displayingCostInfo: values.displayingCostInfo ?? true,
        splitPercentage: values.splitPercentage ?? 50,
        accountIds: values.accountIds ?? null,
        filter: values.filter ?? null,
        isSoundAlertEnabled: values.isSoundAlertEnabled ?? false,
        isQuantityKilo: values.isQuantityKilo ?? false,
        isUserOnly: values.isUserOnly ?? true,
        onlyPending: values.onlyPending ?? false,
        onlyResults: values.onlyResults ?? false,
        onlyUserActivity: values.onlyUserActivity ?? false,
        formData: values.formData ?? {
            accountId: null,
            symbol: '',
            quantity: 0,
            providerId: null,
        },
        quickSendButtons: values.quickSendButtons ?? [],
        costInfoTableData: values.costInfoTableData ?? {
            columnTemplate: null,
        },
        resultsTableColumns: values.resultsTableColumns ?? undefined,
        autoAcceptType: values.autoAcceptType ?? 'percent',
        autoAcceptPercent: values.autoAcceptPercent ?? 1,
    };
};

export const createDefaultAlertsWidget = (values: Partial<AlertsWidgetViewModel> = {}): AlertsWidgetViewModel => {
    return {
        type: 'alerts',
        channelId: values.channelId ?? null,
        activeAlertsTab: values.activeAlertsTab ?? 'symbols',
    };
};
